'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import type { Module, Lesson } from '@/lib/mockData';
import { toast } from 'sonner';

interface ModuleCardProps {
  module: Module;
  lessons: Lesson[];
}

export default function ModuleCard({ module, lessons }: ModuleCardProps) {
  const [expanded, setExpanded] = useState(false);

  const gradeBadgeVariant =
    module.gradeBand === 'Elementary' ?'elementary'
      : module.gradeBand === 'Middle School' ?'middle'
      : module.gradeBand === 'High School' ?'high' :'default';

  const handleBuyBundle = () => {
    // TODO: Backend integration — redirect to TpT product URL or Stripe checkout for module bundle
    toast.success('Opening Teachers Pay Teachers…', {
      description: `${module.title} bundle — $${module.price}`,
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col">
      {/* Header image */}
      <div className={`relative h-44 bg-gradient-to-br ${module.color} overflow-hidden`}>
        <AppImage
          src={module.thumbnail}
          alt={`${module.title} module thumbnail showing ${module.topics[0].toLowerCase()} educational theme`}
          fill
          sizes="(max-width: 1024px) 100vw, 33vw"
          className="object-cover opacity-30 mix-blend-overlay"
        />
        <div className="absolute inset-0 p-5 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <Badge variant={gradeBadgeVariant} className="!bg-white/20 !text-white !border-white/30">
              {module.gradeBand}
            </Badge>
            <span className="text-white/80 text-xs font-semibold bg-white/10 px-2.5 py-1 rounded-full">
              {module.duration}
            </span>
          </div>
          <div>
            <h3 className="font-display font-bold text-white text-xl leading-tight mb-1">
              {module.title}
            </h3>
            <div className="flex items-center gap-2 text-white/70 text-xs">
              <Icon name="BookOpenIcon" size={13} />
              <span>{module.lessonCount} lessons</span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-5 gap-3">
        <p className="text-sm text-stone-600 leading-relaxed line-clamp-2">
          {module.description}
        </p>

        {/* Topics */}
        <div className="flex flex-wrap gap-1.5">
          {module.topics.map((topic) => (
            <Badge key={`mod-topic-${module.id}-${topic}`} variant="topic">
              {topic}
            </Badge>
          ))}
        </div>

        {/* SDGs */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {module.sdgs.slice(0, 5).map((sdg) => (
            <span
              key={`mod-sdg-${module.id}-${sdg}`}
              className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-semibold border border-blue-100"
            >
              SDG {sdg}
            </span>
          ))}
          {module.sdgs.length > 5 && (
            <span className="text-xs text-stone-400">+{module.sdgs.length - 5}</span>
          )}
        </div>

        {/* Expandable lessons */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-between w-full py-2 text-sm font-medium text-stone-600 hover:text-stone-800 transition-colors duration-150 border-t border-stone-100 mt-1"
        >
          <span>Preview lessons</span>
          <Icon
            name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={16}
            className={`transition-transform duration-200 ${expanded ? 'text-primary-600' : 'text-stone-400'}`}
          />
        </button>

        {expanded && (
          <div className="space-y-2 animate-fade-in">
            {lessons.map((lesson, i) => (
              <div
                key={`modlesson-${module.id}-${lesson.id}`}
                className="flex items-center gap-3 py-2 px-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors"
              >
                <span className="w-5 h-5 rounded-full bg-stone-200 text-stone-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-stone-700 line-clamp-1">{lesson.title}</p>
                  <p className="text-xs text-stone-400">{lesson.duration}</p>
                </div>
                <Link
                  href="/lesson-detail"
                  className="p-1 rounded-lg text-stone-400 hover:text-primary-600 hover:bg-primary-50 transition-all duration-150"
                  aria-label={`View ${lesson.title}`}
                >
                  <Icon name="EyeIcon" size={14} />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 mt-auto">
          <button
            onClick={handleBuyBundle}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 active:scale-95 transition-all duration-150"
          >
            <Icon name="ShoppingBagIcon" size={15} />
            Buy Bundle — ${module.price}
          </button>
          <Link
            href="/subscription"
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 active:scale-95 transition-all duration-150 border border-stone-200"
            title="Subscribe for unlimited module access"
          >
            <Icon name="SparklesIcon" size={15} />
          </Link>
        </div>
        <p className="text-xs text-center text-stone-400">
          Or get all modules with a{' '}
          <Link href="/subscription" className="text-primary-600 hover:underline">
            Teacher Pro subscription
          </Link>
        </p>
      </div>
    </div>
  );
}